import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextValue {
  session: Session | null;
  isSuperuser: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const lastUserIdRef = useRef<string | null>(null);

  const checkSuperuser = async () => {
    try {
      const { data, error } = await supabase.rpc("is_superuser" as never);
      if (error) {
        console.error("is_superuser RPC error:", error);
        setIsSuperuser(false);
      } else {
        setIsSuperuser(Boolean(data));
      }
    } catch (err) {
      console.error("is_superuser RPC threw:", err);
      setIsSuperuser(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!mounted) return;
        setSession(newSession);

        if (event === "SIGNED_OUT" || !newSession) {
          lastUserIdRef.current = null;
          setIsSuperuser(false);
          setIsLoading(false);
          return;
        }

        const incomingUserId = newSession.user.id;

        // SIGNED_IN or TOKEN_REFRESHED for the same user means
        // Supabase refreshed credentials — not a new sign-in.
        // Superuser status cannot change during a credential refresh.
        // Skip setIsLoading(true) to prevent unmounting the child tree.
        if (
          (event === "TOKEN_REFRESHED" || event === "SIGNED_IN") &&
          incomingUserId === lastUserIdRef.current
        ) {
          return;
        }

        // Genuine sign-in: new user or first authentication.
        lastUserIdRef.current = incomingUserId;
        setIsLoading(true);
        setTimeout(async () => {
          if (!mounted) return;
          await checkSuperuser();
          if (mounted) setIsLoading(false);
        }, 0);
      }
    );

    // Initial session fetch
    supabase.auth.getSession().then(async ({ data: { session: initialSession } }) => {
      if (!mounted) return;
      setSession(initialSession);
      if (initialSession) {
        await checkSuperuser();
      }
      if (mounted) setIsLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setIsSuperuser(false);
  };

  return (
    <AuthContext.Provider value={{ session, isSuperuser, isLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
